import { useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faComments } from "@fortawesome/free-solid-svg-icons"
import Loader from "../../components/Loader"
import FeedbackResponseCard from "../../components/FeedbackResponseCard"
import { useVideoScanSummary } from "../../hooks/useVideoLogs"
import { formatISODate } from "../../utils/datetime"

const Stat = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-lg font-bold text-gray-900 leading-tight">
      {value}
    </span>
    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
      {label}
    </span>
  </div>
)

/*
  Feedback responses for one report, without leaving the analytics table.

  Reuses the scan-summary endpoint the Video Logs engagement card already calls,
  so opening the drawer costs one cached request per report rather than a new
  screen and a manual hunt through Customer Feedback.
*/
const FeedbackDrawer = ({ report, onClose }) => {
  const uuid = report?.ReportVideosUuid
  const { data, isLoading } = useVideoScanSummary(uuid)

  const feedback = data?.feedback || []

  // Escape closes, matching the backdrop click.
  useEffect(() => {
    if (!uuid) return

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose()
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [uuid, onClose])

  if (!report) return null

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* 39.6rem — 10% wider than Tailwind's max-w-xl (36rem). */}
      <div className="fixed top-0 right-0 h-full w-full max-w-[39.6rem] bg-gray-50 shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-600 mb-1">
                Patient feedback
              </p>
              <h2 className="text-xl font-bold text-gray-900 truncate">
                {report.AccessionNo || "—"}
              </h2>
            </div>

            <button
              className="text-gray-400 hover:text-gray-700 transition-colors p-2 -mr-2 shrink-0"
              title="Close"
              onClick={onClose}
            >
              <FontAwesomeIcon className="w-4 h-4" icon={faTimes} />
            </button>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-4 mt-4 pt-4 border-t border-gray-100">
            <Stat
              label="Body part"
              value={
                <span className="text-sm font-semibold text-gray-800">
                  {report.BodyPart?.toUpperCase() || "—"}
                </span>
              }
            />
            <Stat
              label="Patient issuer"
              value={
                <span className="text-sm font-semibold text-gray-800">
                  {report.PatientIssuer || "—"}
                </span>
              }
            />
            <Stat label="Total scans" value={report.TotalScans} />
            <Stat label="Unique scans" value={report.UniqueScans} />
            <Stat label="Responses" value={report.FeedbackSubmittedCount} />
            <Stat
              label="Last scan"
              value={
                <span className="text-xs font-medium text-gray-700">
                  {formatISODate(report.LastScanAt) || "—"}
                </span>
              }
            />
          </div>
        </div>

        {/* Responses */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center gap-3 py-16">
              <Loader />
              <span className="text-sm text-gray-600">Loading feedback...</span>
            </div>
          ) : feedback.length > 0 ? (
            <div className="flex flex-col gap-3">
              {feedback.map((response, index) => (
                <FeedbackResponseCard
                  key={response.CustomerFeedbackId}
                  index={feedback.length > 1 ? index : null}
                  response={response}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <FontAwesomeIcon
                className="w-8 h-8 text-gray-300"
                icon={faComments}
              />
              <p className="text-sm text-gray-500">
                No feedback responses for this report.
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 bg-white px-6 py-3">
          <p className="text-[11px] text-gray-400">
            Responses are anonymous — no patient name, email, or identifying
            information is collected.
          </p>
        </div>
      </div>
    </>
  )
}

export default FeedbackDrawer
